import {
	CompactSelect,
	CompactTextInput,
	type CompactSelectOption,
} from "newui/components/ui/InputWithLabel";
import { cn } from "utils/helpers";

import type { ColumnConfig } from "../../SpreadsheetEditor";
import { quoteToScheduleType } from "../schedule-column-type-mapping";
import type { ScheduleRowDraft } from "./ScheduleRowsBody";

const CELL_BASE = "border border-[#EAECF0] px-1 py-1 text-sm";

export interface ScheduleRowCellProps {
	column: ColumnConfig;
	row: ScheduleRowDraft;
	rowIndex: number;
	unitOptions: CompactSelectOption[];
	hasError?: boolean;
	onChangeCell: (rowId: number, columnLocalId: string, value: string) => void;
	onBlurCurrency: (rowId: number, columnLocalId: string) => void;
}

/**
 * Renders a single editable schedule-of-rates cell. The variant is chosen
 * from the column's QuoteColumnType mapped to ScheduleColumnType:
 *  - text                 -> CompactTextInput
 *  - number               -> CompactTextInput (type=number, inputMode=decimal)
 *  - currency             -> CompactTextInput + onBlur currency normalisation
 *  - unit_of_measurement  -> CompactSelect bound to the units list
 *
 * Values are stored keyed by the column's stable `localId` so renaming or
 * retyping a column does not orphan a cell.
 */
export const ScheduleRowCell: React.FC<ScheduleRowCellProps> = ({
	column,
	row,
	rowIndex,
	unitOptions,
	hasError = false,
	onChangeCell,
	onBlurCurrency,
}) => {
	const scheduleType = quoteToScheduleType(column.columnType);
	const value = row.values[column.localId] ?? "";
	const ariaSuffix = `row ${rowIndex + 1}, ${column.label || scheduleType}`;

	if (scheduleType === "unit_of_measurement") {
		return (
			<td role="gridcell" className={CELL_BASE} style={{ minWidth: 140 }}>
				<CompactSelect
					value={value}
					hasError={hasError}
					placeholder="Select unit"
					options={unitOptions}
					onChange={(e) =>
						onChangeCell(row.id, column.localId, e.target.value)
					}
					aria-label={`Unit of measurement for ${ariaSuffix}`}
					data-testid={`schedule-cell-${row.id}-${column.localId}`}
				/>
			</td>
		);
	}

	if (scheduleType === "currency") {
		return (
			<td role="gridcell" className={CELL_BASE}>
				<CompactTextInput
					inputMode="decimal"
					value={value}
					hasError={hasError}
					placeholder="0.00"
					onChange={(e) =>
						onChangeCell(row.id, column.localId, e.target.value)
					}
					onBlur={() => onBlurCurrency(row.id, column.localId)}
					aria-label={`Currency value for ${ariaSuffix}`}
					data-testid={`schedule-cell-${row.id}-${column.localId}`}
				/>
			</td>
		);
	}

	if (scheduleType === "number") {
		return (
			<td role="gridcell" className={CELL_BASE}>
				<CompactTextInput
					type="number"
					inputMode="decimal"
					value={value}
					hasError={hasError}
					onChange={(e) =>
						onChangeCell(row.id, column.localId, e.target.value)
					}
					aria-label={`Number value for ${ariaSuffix}`}
					data-testid={`schedule-cell-${row.id}-${column.localId}`}
				/>
			</td>
		);
	}

	// text (default)
	return (
		<td role="gridcell" className={cn(CELL_BASE)}>
			<CompactTextInput
				value={value}
				hasError={hasError}
				onChange={(e) => onChangeCell(row.id, column.localId, e.target.value)}
				aria-label={`Text value for ${ariaSuffix}`}
				data-testid={`schedule-cell-${row.id}-${column.localId}`}
			/>
		</td>
	);
};

export default ScheduleRowCell;
